import { create } from 'zustand';
import { Clip as Recording } from '@goldcast/player-config/src/types/clip-types';
import { getIsDuplicateRecording } from '@goldcast/player-config/src/utils/clipUtils';
import { loadClips as fetchRecordings } from '@/libs/clipContentUtil';
import { showErrorToast } from '@/libs/toast/toast';

interface RecordingDuplicatesState {
  recordings: Record<string, Recording[]>;
  isLoading: boolean;
  overwriteRecordings: (broadcastId: string, recordings: Recording[]) => void;
  appendRecording: (broadcastId: string, recording: Recording) => void;
  updateRecording: (broadcastId: string, updated: Recording) => void;
  removeRecording: (broadcastId: string, recordingId: string) => void;
  clearRecordings: (broadcastId?: string) => void;
  loadRecordings: (broadcastId: string) => Promise<void>;
}

export const useRecordingDuplicatesStore = create<RecordingDuplicatesState>()((set, get) => ({
  recordings: {},
  isLoading: false,

  overwriteRecordings: (broadcastId, recordings) =>
    set(state => ({
      recordings: {
        ...state.recordings,
        // The store only holds duplicate recordings; the original is surfaced separately by the
        // dropdown, so keeping it here would render it twice.
        [broadcastId]: recordings.filter(getIsDuplicateRecording)
      }
    })),

  appendRecording: (broadcastId, recording) =>
    set(state => ({
      recordings: { ...state.recordings, [broadcastId]: [recording, ...(state.recordings[broadcastId] ?? [])] }
    })),

  updateRecording: (broadcastId, updated) =>
    set(state => ({
      recordings: {
        ...state.recordings,
        [broadcastId]: (state.recordings[broadcastId] ?? []).map(r => (r.id === updated.id ? updated : r))
      }
    })),

  removeRecording: (broadcastId, recordingId) =>
    set(state => ({
      recordings: {
        ...state.recordings,
        [broadcastId]: (state.recordings[broadcastId] ?? []).filter(r => r.id !== recordingId)
      }
    })),

  clearRecordings: broadcastId => {
    if (broadcastId) {
      set(state => ({ recordings: { ...state.recordings, [broadcastId]: [] } }));
    } else {
      set({ recordings: {} });
    }
  },

  loadRecordings: async broadcastId => {
    // if ((get().recordings[broadcastId]?.length ?? 0) > 0) {
    //   return;
    // }

    set({ isLoading: true });
    try {
      const allClipsAndDuplicates = await fetchRecordings(broadcastId, { includeDuplicates: true });
      const onlyDuplicates = allClipsAndDuplicates.filter(getIsDuplicateRecording);
      get().overwriteRecordings(broadcastId, onlyDuplicates);
    } catch {
      get().clearRecordings(broadcastId);
      showErrorToast('Failed to load duplicate recordings. Please try again.');
    } finally {
      set({ isLoading: false });
    }
  }
}));

// Standalone helpers for non-React callers
export const overwriteRecordingDuplicates = (broadcastId: string, recordings: Recording[]) =>
  useRecordingDuplicatesStore.getState().overwriteRecordings(broadcastId, recordings);

export const appendRecordingDuplicate = (broadcastId: string, recording: Recording) =>
  useRecordingDuplicatesStore.getState().appendRecording(broadcastId, recording);

export const updateRecordingDuplicate = (broadcastId: string, updated: Recording) =>
  useRecordingDuplicatesStore.getState().updateRecording(broadcastId, updated);

export const removeRecordingDuplicate = (broadcastId: string, recordingId: string) =>
  useRecordingDuplicatesStore.getState().removeRecording(broadcastId, recordingId);

export const clearRecordingDuplicates = (broadcastId?: string) =>
  useRecordingDuplicatesStore.getState().clearRecordings(broadcastId);

export const loadRecordingDuplicates = (broadcastId: string) =>
  useRecordingDuplicatesStore.getState().loadRecordings(broadcastId);
